import React, { useState, useEffect, Fragment } from 'react';
import { ArrowLeft, Save, X, Truck, Package, MapPin } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getDeliveryProfile, createDeliveryProfile, updateDeliveryProfile } from '../../actions/deliveryProfile';
import { connect } from 'react-redux';
import { getProfiles } from '../../actions/profile';
import { getServiceableAreas } from '../../actions/serviceableArea';
import { getSuperServiceableAreas } from '../../actions/superServiceableArea';
import { setAlert } from '../../actions/alert';

const DeliveryProfile = ({
    getDeliveryProfile,
    createDeliveryProfile,
    updateDeliveryProfile,
    setAlert,
    deliveryProfile,
    loading,
    profiles,
    serviceableAreas,
    superServiceableAreas,
    getProfiles,
    getServiceableAreas,
    getSuperServiceableAreas
}) => {
    const [areaType, setAreaType] = useState('serviceableArea');
    const [formData, setFormData] = useState({
        productProfile: '',
        serviceableArea: '',
        superServiceableArea: ''
    });

    const [errors, setErrors] = useState({
        productProfile: '',
        serviceableArea: ''
    });

    const { id } = useParams();
    const navigate = useNavigate();

    // Fetch data on component mount
    useEffect(() => {
        document.title = 'Delivery Profile Details';
        
        // Fetch profiles and serviceable areas for dropdowns
        if (!profiles || profiles.length === 0) {
            getProfiles();
        }
        if (!serviceableAreas || serviceableAreas.length === 0) {
            getServiceableAreas();
        }
        if (!superServiceableAreas || superServiceableAreas.length === 0) {
            getSuperServiceableAreas();
        }
    }, []);

    // Handle delivery profile data loading and form population
    useEffect(() => {
        if (id !== 'new') {
            if (!deliveryProfile || (deliveryProfile._id !== id) || loading) {
                getDeliveryProfile(id);
            }
        }
    }, [id]);

    // Handle form population when delivery profile data is loaded
    useEffect(() => {
        if (id !== 'new' && deliveryProfile && deliveryProfile._id === id) {
            setFormData({
                productProfile: deliveryProfile.productProfile || '',
                serviceableArea: deliveryProfile.serviceableArea || '',
                superServiceableArea: deliveryProfile.superServiceableArea || ''
            });
            setAreaType(deliveryProfile.superServiceableArea ? 'superServiceableArea' : 'serviceableArea');
        }
    }, [deliveryProfile, id]);
  
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error when typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const handleAreaTypeChange = (e) => {
        const newAreaType = e.target.value;
        setAreaType(newAreaType);
        // Area types are mutually exclusive, clear the one not in use
        setFormData({
            ...formData,
            serviceableArea: newAreaType === 'serviceableArea' ? formData.serviceableArea : '',
            superServiceableArea: newAreaType === 'superServiceableArea' ? formData.superServiceableArea : ''
        });
        setErrors({
            ...errors,
            serviceableArea: '',
            superServiceableArea: ''
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        // Basic validation
        const newErrors = {};
        if (!formData.productProfile) {
            newErrors.productProfile = 'Product profile is required';
        }
        if (areaType === 'serviceableArea' && !formData.serviceableArea) {
            newErrors.serviceableArea = 'Serviceable area is required';
        }
        if (areaType === 'superServiceableArea' && !formData.superServiceableArea) {
            newErrors.superServiceableArea = 'Super serviceable area is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Area types are mutually exclusive - only send the one in use
        const payload = {
            productProfile: formData.productProfile,
            serviceableArea: areaType === 'serviceableArea' ? formData.serviceableArea : null,
            superServiceableArea: areaType === 'superServiceableArea' ? formData.superServiceableArea : null
        };

        try {
            if (id === 'new') {
                // Create new delivery profile
                const result = await createDeliveryProfile(payload);
                if (result) {
                    // Navigate to the created delivery profile page
                    navigate(`/inventory/v1.2/delivery-profiles/${result._id}`);
                }
            } else {
                // Update existing delivery profile
                const dataToUpdate = {
                    ...payload,
                    _id: id // Ensure the delivery profile ID is included
                };
                const result = await updateDeliveryProfile(id, dataToUpdate);
                if (result) {
                    // Delivery profile updated successfully
                    console.log('Delivery profile updated successfully');
                }
            }
        } catch (error) {
            console.error('Error saving delivery profile:', error);
        }
    };
  
    const handleReset = () => {
        setAreaType('serviceableArea');
        setFormData({
            productProfile: '',
            serviceableArea: '',
            superServiceableArea: ''
        });

        setErrors({
            productProfile: '',
            serviceableArea: '',
            superServiceableArea: ''
        });
    };

    return (
        <Fragment>
            <div className='container-fluid py-3'>
                {/* Header */}
                <div className='row mb-3'>
                    <div className='col-12'>
                        <div className='d-flex justify-content-between align-items-center'>
                            <Link className='btn btn-outline-primary btn-sm' to='/inventory/v1.2/delivery-profiles'>
                                <ArrowLeft size={16} className="me-2" />
                                Back to Delivery Profiles
                            </Link>
                            <h4 className='text-primary mb-0 d-flex align-items-center'>
                                <Truck size={24} className="me-2" />
                                {id === 'new' ? 'Create New Delivery Profile' : 'Edit Delivery Profile'}
                            </h4>
                            <div></div>
                        </div>
                    </div>
                </div>

                <form id="delivery-profile-form" onSubmit={onSubmit}>
                    <div className='row'>
                        {/* Main Form Content */}
                        <div className='col-12'>
                            {/* Basic Information Card */}
                            <div className='card shadow-sm mb-3'>
                                <div className='card-header bg-primary text-white py-2'>
                                    <h6 className='mb-0 d-flex align-items-center'>
                                        <Truck size={16} className="me-2" />
                                        Delivery Profile Configuration
                                    </h6>
                                </div>
                                <div className='card-body py-3'>
                                    <div className='mb-4'>
                                        <label className='form-label fw-bold small d-flex align-items-center mb-2'>
                                            <MapPin size={16} className="me-2 text-warning" />
                                            Area Type
                                        </label>
                                        <div className='d-flex border rounded overflow-hidden' style={{ maxWidth: '480px' }}>
                                            <button
                                                type="button"
                                                className={`btn btn-sm flex-fill rounded-0 border-0 ${areaType === 'serviceableArea' ? 'btn-primary' : 'btn-light'}`}
                                                onClick={() => handleAreaTypeChange({ target: { value: 'serviceableArea' } })}
                                            >
                                                Serviceable Area
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm flex-fill rounded-0 border-0 border-start ${areaType === 'superServiceableArea' ? 'btn-primary' : 'btn-light'}`}
                                                onClick={() => handleAreaTypeChange({ target: { value: 'superServiceableArea' } })}
                                            >
                                                Super Serviceable Area
                                            </button>
                                        </div>
                                        <div className='form-text mt-1'>
                                            Super Serviceable Area is a scheduled-delivery-only zone outside the normal serviceable areas.
                                        </div>
                                    </div>
                                    <div className='row'>
                                        <div className='col-md-6 mb-3'>
                                            <label className='form-label fw-bold small d-flex align-items-center'>
                                                <Package size={16} className="me-2 text-success" />
                                                Product Profile <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                name="productProfile"
                                                value={formData.productProfile}
                                                onChange={handleChange}
                                                className={`form-select ${errors.productProfile ? 'is-invalid' : ''}`}
                                                required
                                            >
                                                <option value="">Select Product Profile</option>
                                                {profiles && profiles.map((profile) => (
                                                    <option key={profile._id} value={profile._id}>
                                                        {profile.name || profile._id}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.productProfile && (
                                                <div className="invalid-feedback">
                                                    {errors.productProfile}
                                                </div>
                                            )}
                                        </div>
                                        <div className='col-md-6 mb-3'>
                                            <label className='form-label fw-bold small d-flex align-items-center'>
                                                <MapPin size={16} className="me-2 text-warning" />
                                                {areaType === 'serviceableArea' ? 'Serviceable Area' : 'Super Serviceable Area'}
                                                {' '}<span className="text-danger">*</span>
                                            </label>
                                            {areaType === 'serviceableArea' ? (
                                                <>
                                                    <select
                                                        name="serviceableArea"
                                                        value={formData.serviceableArea}
                                                        onChange={handleChange}
                                                        className={`form-select ${errors.serviceableArea ? 'is-invalid' : ''}`}
                                                        required
                                                    >
                                                        <option value="">Select Serviceable Area</option>
                                                        {serviceableAreas && serviceableAreas.map((area) => (
                                                            <option key={area._id} value={area._id}>
                                                                {area.name || area._id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.serviceableArea && (
                                                        <div className="invalid-feedback">
                                                            {errors.serviceableArea}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <select
                                                        name="superServiceableArea"
                                                        value={formData.superServiceableArea}
                                                        onChange={handleChange}
                                                        className={`form-select ${errors.superServiceableArea ? 'is-invalid' : ''}`}
                                                        required
                                                    >
                                                        <option value="">Select Super Serviceable Area</option>
                                                        {superServiceableAreas && superServiceableAreas.map((area) => (
                                                            <option key={area._id} value={area._id}>
                                                                {area.name || area._id}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.superServiceableArea && (
                                                        <div className="invalid-feedback">
                                                            {errors.superServiceableArea}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Information Card */}
                                    <div className='card bg-light border-0 mt-3'>
                                        <div className='card-body py-2'>
                                            <div className='row'>
                                                <div className='col-md-6'>
                                                    <div className='d-flex align-items-center'>
                                                        <Package size={16} className="text-success me-2" />
                                                        <div>
                                                            <div className='small text-muted'>Selected Product Profile</div>
                                                            <div className='small fw-bold'>
                                                                {formData.productProfile ? 
                                                                    (profiles?.find(p => p._id === formData.productProfile)?.name || formData.productProfile) : 
                                                                    'None selected'
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='col-md-6'>
                                                    <div className='d-flex align-items-center'>
                                                        <MapPin size={16} className="text-warning me-2" />
                                                        <div>
                                                            <div className='small text-muted'>
                                                                Selected {areaType === 'serviceableArea' ? 'Serviceable Area' : 'Super Serviceable Area'}
                                                            </div>
                                                            <div className='small fw-bold'>
                                                                {areaType === 'serviceableArea' ? (
                                                                    formData.serviceableArea ?
                                                                        (serviceableAreas?.find(a => a._id === formData.serviceableArea)?.name || formData.serviceableArea) :
                                                                        'None selected'
                                                                ) : (
                                                                    formData.superServiceableArea ?
                                                                        (superServiceableAreas?.find(a => a._id === formData.superServiceableArea)?.name || formData.superServiceableArea) :
                                                                        'None selected'
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Description Card */}
                            <div className='card shadow-sm mb-3'>
                                <div className='card-header bg-info text-white py-2'>
                                    <h6 className='mb-0'>
                                        <i className='fas fa-info-circle me-2'></i>
                                        About Delivery Profiles
                                    </h6>
                                </div>
                                <div className='card-body py-3'>
                                    <div className='alert alert-info mb-0'>
                                        <h6 className='alert-heading'>What is a Delivery Profile?</h6>
                                        <p className='mb-2'>
                                            A delivery profile creates a link between a product profile and a serviceable area, 
                                            defining which products are available for delivery in specific geographical regions.
                                        </p>
                                        <hr />
                                        <p className='mb-0 small'>
                                            <strong>Use Cases:</strong> Product availability control, delivery configuration, 
                                            geographic restrictions, and delivery management.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                {/* Actions Section - Bottom */}
                <div className='row mt-3'>
                    <div className='col-12'>
                        <div className='card shadow-sm'>
                            <div className='card-body text-center py-3'>
                                {id === 'new' && (
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        className="btn btn-outline-secondary btn-sm me-2"
                                    >
                                        <X size={14} className="me-1" />
                                        Reset
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    form="delivery-profile-form"
                                    className="btn btn-primary btn-sm"
                                    disabled={loading}
                                >
                                    <Save size={14} className="me-1" />
                                    {loading ? 'Saving...' : (id === 'new' ? 'Create Profile' : 'Update Profile')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

DeliveryProfile.propTypes = {
    getDeliveryProfile: PropTypes.func.isRequired,
    createDeliveryProfile: PropTypes.func.isRequired,
    updateDeliveryProfile: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    deliveryProfile: PropTypes.object,
    getProfiles: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    getSuperServiceableAreas: PropTypes.func.isRequired,
    profiles: PropTypes.array,
    serviceableAreas: PropTypes.array,
    superServiceableAreas: PropTypes.array
};

const mapStateToProps = state => ({
    loading: state.deliveryProfile.loading,
    deliveryProfile: state.deliveryProfile.deliveryProfile,
    profiles: state.profile.profiles,
    serviceableAreas: state.serviceableArea.serviceableAreas,
    superServiceableAreas: state.superServiceableArea.superServiceableAreas
});

export default connect(mapStateToProps, {
    getDeliveryProfile,
    createDeliveryProfile,
    updateDeliveryProfile,
    setAlert,
    getProfiles,
    getServiceableAreas,
    getSuperServiceableAreas
})(DeliveryProfile);
